import { useEffect, useRef, useState } from 'preact/hooks';
import { AlertTriangle, Check, Copy, MapPin, Phone, Volume2 } from 'lucide-preact';
import { routeStore } from '../state/routeStore';
import { locale, t } from '../i18n';
import { getEmergencyService } from '../lib/emergencyNumber';
import { armChime, chimeMuted, playChime } from '../lib/audio';

const REPEAT_INTERVAL_MS = 30_000;
const CLEARED_STICKY_MS = 5 * 60_000;

/**
 * Highest-stakes screen in the SPA. Renders when `route.emergency.state ===
 * 'active'`. Many watchers arrive here directly from an emergency SMS sent
 * by api repo issue #134, possibly relatives in panic — copy is calm,
 * actions are big and obvious, the "Call" button leads.
 *
 * Repeats the chime every 30s while emergency active and the tab is
 * foregrounded. Pauses cleanly on `visibilitychange`. Respects the user's
 * saved mute preference and `prefers-reduced-motion`.
 *
 * No-op (returns null) until the API actually emits an emergency subdoc —
 * the SPA reads it when #134 lands; no SPA code change needed then.
 */
export function EmergencyBanner() {
  const route = routeStore.value;
  const emergency = route?.emergency ?? null;
  const [audioArmed, setAudioArmed] = useState(false);
  const [muted, setMuted] = useState(chimeMuted());
  const [now, setNow] = useState(Date.now());
  const lastTriggeredAt = useRef<string | null>(null);

  // Tick once a second so relative-time copy updates without a new event.
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(handle);
  }, []);

  // Chime trigger — fires on new emergency activation, then repeats while
  // active + foregrounded. Cleaned up when emergency clears or unmounts.
  useEffect(() => {
    if (!emergency || emergency.state !== 'active') return;
    const triggerStamp = emergency.triggeredAt;
    const isNewTrigger = lastTriggeredAt.current !== triggerStamp;
    lastTriggeredAt.current = triggerStamp;
    if (isNewTrigger) playChime();
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (document.visibilityState === 'visible') playChime();
      }, REPEAT_INTERVAL_MS);
    };
    const stop = () => {
      if (interval) clearInterval(interval);
      interval = null;
    };
    start();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [emergency?.state, emergency?.triggeredAt]);

  // Hide cleared banner after the 5-minute sticky window.
  if (!emergency) return null;
  if (emergency.state === 'cleared') {
    const clearedAt = emergency.clearedAt ? new Date(emergency.clearedAt).getTime() : NaN;
    if (Number.isFinite(clearedAt) && now - clearedAt > CLEARED_STICKY_MS) return null;
  }

  const name = route?.creator?.name?.trim() || route?.creator?.username || '—';
  const service = getEmergencyService(locale.value);
  const triggeredAgo = formatRelativePast(emergency.triggeredAt, now);
  const locationLabel = formatLocation(emergency.location);
  const coordsString = `${emergency.location.lat.toFixed(5)}, ${emergency.location.lng.toFixed(5)}`;
  const mapsUrl = buildMapsUrl(emergency.location.lat, emergency.location.lng, name);

  if (emergency.state === 'cleared') {
    const time = emergency.clearedAt
      ? new Date(emergency.clearedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : '';
    return (
      <div
        class="pointer-events-auto fixed inset-x-0 top-0 z-40 bg-safety text-white px-4 py-3 shadow-lg"
        role="status"
        aria-live="polite"
      >
        <div class="mx-auto flex max-w-2xl items-center gap-3">
          <Check size={20} aria-hidden="true" />
          <p class="text-sm font-medium">{t('emergency.bannerCleared', { name, time })}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      class="pointer-events-auto fixed inset-x-0 top-0 z-40"
      role="alert"
      aria-live="assertive"
    >
      <div class="bg-emergency text-white shadow-2xl">
        <div class="mx-auto max-w-2xl px-4 py-3 flex items-start gap-3">
          <AlertTriangle size={28} class="shrink-0 mt-0.5 motion-safe:animate-pulse" aria-hidden="true" />
          <div class="flex-1 min-w-0">
            <p class="text-base font-bold leading-tight">
              {t('emergency.bannerActive', { name })}
            </p>
            <p class="text-xs opacity-90 mt-0.5">
              {t('emergency.triggeredAgo', { relative: triggeredAgo })}
            </p>
          </div>
        </div>
        <div class="bg-emergency/90 px-4 pb-3">
          <div class="mx-auto max-w-2xl">
            <p class="text-xs uppercase tracking-wide opacity-80 mb-1">{t('emergency.lastKnown')}</p>
            <p class="text-sm font-medium break-words">{locationLabel}</p>
            {!emergency.location.address && (
              <p class="text-xs opacity-80 font-mono mt-0.5">{coordsString}</p>
            )}
          </div>
        </div>
      </div>

      <div class="bg-surface px-4 py-3 shadow-md">
        <div class="mx-auto max-w-2xl space-y-2">
          <a
            href={service.telUri}
            class="flex items-center justify-center gap-2 rounded-xl bg-emergency px-4 py-3 text-white font-semibold text-base shadow-sm hover:bg-emergency-accent transition-colors"
          >
            <Phone size={18} aria-hidden="true" />
            {t('emergency.callServices', { number: service.displayNumber })}
          </a>
          <div class="flex gap-2">
            <button
              type="button"
              class="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-text hover:bg-surface-muted"
              onClick={() => void copyToClipboard(`${locationLabel} (${coordsString})`)}
            >
              <Copy size={14} aria-hidden="true" />
              {t('emergency.copyLocation')}
            </button>
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-text hover:bg-surface-muted"
            >
              <MapPin size={14} aria-hidden="true" />
              {t('emergency.openInMaps')}
            </a>
          </div>
          {!audioArmed && !muted && (
            <button
              type="button"
              class="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-warning-soft text-warning px-3 py-1.5 text-xs font-medium"
              onClick={() => {
                armChime();
                setAudioArmed(true);
                // Fire one immediately on the gesture so the watcher hears it.
                setTimeout(playChime, 100);
              }}
            >
              <Volume2 size={14} aria-hidden="true" />
              {t('emergency.enableAlerts')}
            </button>
          )}
        </div>
      </div>

      <span class="sr-only">{t('ariaLive.emergency', { name, location: locationLabel })}</span>
    </div>
  );
}

function formatLocation(location: { address?: string | null; lat: number; lng: number }): string {
  if (location.address && location.address.trim()) return location.address.trim();
  return `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`;
}

function formatRelativePast(iso: string, now: number): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h`;
}

function buildMapsUrl(lat: number, lng: number, label: string): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const encoded = encodeURIComponent(label);
  if (/iPhone|iPad|Macintosh/.test(ua)) {
    return `https://maps.apple.com/?q=${encoded}&ll=${lat},${lng}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Older browsers: build a temporary textarea + execCommand fallback.
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand('copy');
    } catch {
      // give up — clipboard not available, user can read the address manually
    }
    document.body.removeChild(el);
  }
}
